import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import SpinnerOverlay from '../../../components/ui/SpinnerOverlay';
import SuccessToast from '../../../components/ui/SuccessToast';

const LAST_PAYMENT_KEY = 'otg_last_payment_method';

const BookingConfirmation = ({ bookingData, onClose, onNewBooking, initialPaymentMethod }) => {
  const navigate = useNavigate();
  const [mpesaNumber, setMpesaNumber] = useState('');
  const [bookingState, setBookingState] = useState('summary'); // summary | processing | confirmed
  const [bookingRef, setBookingRef] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoState, setPromoState] = useState('idle'); // idle | validating | valid | invalid
  const [promoError, setPromoError] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null); // { code_name, discount_type, discount_value }

  const baseFare = bookingData?.fare || 0;

  const calcDiscount = () => {
    if (!appliedPromo) return 0;
    if (appliedPromo?.discount_type === 'percentage') {
      return Math.round((baseFare * appliedPromo?.discount_value) / 100);
    }
    return Math.min(appliedPromo?.discount_value, baseFare);
  };

  const discountAmount = calcDiscount();
  const finalFare = Math.max(0, baseFare - discountAmount);

  const allPaymentMethods = [
    { id: 'mpesa', label: 'M-Pesa', icon: '📱', description: 'Pay via M-Pesa STK Push', color: 'text-green-600' },
    { id: 'cash', label: 'Cash', icon: '💵', description: 'Pay driver directly', color: 'text-[#0a1628]' },
    { id: 'wallet', label: 'OnTheGo Wallet', icon: '👛', description: 'Balance: KSh 1,250', color: 'text-[#0a1628]' },
    { id: 'card', label: 'Card', icon: '💳', description: 'Visa / Mastercard', color: 'text-[#0a1628]' }
  ];

  // Resolve initial payment: prop → localStorage → default 'cash'
  const resolveInitialMethod = () => {
    if (initialPaymentMethod && allPaymentMethods?.find(m => m?.id === initialPaymentMethod)) {
      return initialPaymentMethod;
    }
    const stored = localStorage.getItem(LAST_PAYMENT_KEY);
    if (stored && allPaymentMethods?.find(m => m?.id === stored)) {
      return stored;
    }
    return 'cash';
  };

  const [paymentMethod, setPaymentMethod] = useState(resolveInitialMethod);

  const selectedMethod = allPaymentMethods?.find(m => m?.id === paymentMethod) || allPaymentMethods?.[1];

  const handleSelectMethod = (id) => {
    setPaymentMethod(id);
    setDropdownOpen(false);
  };

  const vehicleLabels = { bike: 'Bike', boda: 'Boda Boda', tuktuk: 'Tuk Tuk', car: 'Car' };

  const handleApplyPromo = async () => {
    const code = promoCode?.trim()?.toUpperCase();
    if (!code) return;

    setPromoState('validating');
    setPromoError('');
    setAppliedPromo(null);

    try {
      const { data, error } = await supabase?.from('promo_codes')?.select('code_name, discount_type, discount_value, min_order_value, expiry_date, usage_limit, usage_count, promo_status, service_type')?.eq('code_name', code)?.single();

      if (error || !data) {
        setPromoState('invalid');
        setPromoError('Promo code not found.');
        return;
      }

      if (data?.promo_status !== 'active') {
        setPromoState('invalid');
        setPromoError('This promo code is no longer active.');
        return;
      }

      if (new Date(data.expiry_date) < new Date()) {
        setPromoState('invalid');
        setPromoError('This promo code has expired.');
        return;
      }

      if (data?.usage_count >= data?.usage_limit) {
        setPromoState('invalid');
        setPromoError('This promo code has reached its usage limit.');
        return;
      }

      if (baseFare < data?.min_order_value) {
        setPromoState('invalid');
        setPromoError(`Minimum order of KSh ${data?.min_order_value} required.`);
        return;
      }

      setAppliedPromo(data);
      setPromoState('valid');
    } catch (err) {
      setPromoState('invalid');
      setPromoError('Unable to validate promo code. Try again.');
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoCode('');
    setPromoState('idle');
    setPromoError('');
  };

  const handleConfirmBooking = () => {
    // Persist last-used payment method
    localStorage.setItem(LAST_PAYMENT_KEY, paymentMethod);
    setBookingState('processing');
    setTimeout(async () => {
      const ref = 'OTG' + Math.random()?.toString(36)?.substr(2, 8)?.toUpperCase();
      setBookingRef(ref);
      setBookingState('confirmed');

      // Try to create order in Supabase and navigate to confirmation screen
      try {
        const { data: orderData, error: orderErr } = await supabase?.from('orders')?.insert({
          order_number: ref,
          pickup_address: bookingData?.pickup || 'Current Location',
          dropoff_address: bookingData?.dropoff || 'Destination',
          vehicle_type: bookingData?.vehicle || 'boda',
          service_type: bookingData?.service || 'ride',
          fare_amount: baseFare,
          platform_fee: Math.round(baseFare * 0.1),
          total_amount: finalFare + Math.round(baseFare * 0.1),
          payment_method: paymentMethod,
          payment_status: 'unpaid',
          status: 'confirmed',
          customer_id: (await supabase?.auth?.getUser())?.data?.user?.id || null,
        })?.select()?.single();

        if (!orderErr && orderData?.id) {
          setTimeout(() => {
            navigate(`/booking-confirmation-screen?orderId=${orderData?.id}`);
          }, 1500);
        }
      } catch (err) {
        // If Supabase insert fails, still show confirmed state
      }
    }, 2000);
  };

  if (bookingState === 'processing') {
    return (
      <>
        <SpinnerOverlay
          visible
          title="Finding your driver..."
          message="Connecting you with the nearest available driver"
        />
      </>
    );
  }

  if (bookingState === 'confirmed') {
    return (
      <div className="fixed inset-0 bg-[#0a1628]/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <SuccessToast
          visible
          message="Booking Confirmed!"
          subMessage={`Ref: ${bookingRef}`}
          duration={4000}
          position="top"
          onDismiss={() => {}}
        />
        <div className="bg-white rounded-3xl p-6 max-w-sm w-full">
          {/* Success Header */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-8 h-8 text-green-600">
                <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-[#0a1628]">Booking Confirmed!</h3>
            <p className="text-sm text-[#0a1628]/60 mt-1">Ref: <span className="font-mono font-bold text-[#0a1628]">{bookingRef}</span></p>
          </div>

          {/* Driver Card */}
          <div className="bg-[#0a1628] rounded-2xl p-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#ffd700] flex items-center justify-center text-xl font-bold text-[#0a1628]">
                JM
              </div>
              <div className="flex-1">
                <p className="font-bold text-white">James Mwangi</p>
                <div className="flex items-center gap-1 mt-0.5">
                  <svg viewBox="0 0 24 24" fill="#ffd700" className="w-3.5 h-3.5">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                  <span className="text-xs text-white/80">4.9 · 1,234 trips</span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-white/60">Plate</p>
                <p className="text-sm font-bold text-[#ffd700]">KBZ 123A</p>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                <span className="text-xs text-white/70">En route to pickup</span>
              </div>
              <span className="text-sm font-bold text-[#ffd700]">~3 min away</span>
            </div>
          </div>

          {/* Trip Summary */}
          <div className="bg-[#0a1628]/5 rounded-xl p-3.5 mb-4 space-y-2">
            <div className="flex items-start gap-2">
              <div className="w-2 h-2 rounded-full bg-[#ffd700] mt-1.5 flex-shrink-0"></div>
              <p className="text-sm text-[#0a1628]">{bookingData?.pickup || 'Current Location'}</p>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-2 h-2 rounded-sm bg-[#0a1628] rotate-45 mt-1.5 flex-shrink-0"></div>
              <p className="text-sm text-[#0a1628]">{bookingData?.dropoff || 'Destination'}</p>
            </div>
            {appliedPromo && (
              <div className="pt-2 mt-2 border-t border-[#0a1628]/10 space-y-1">
                <div className="flex justify-between text-xs text-[#0a1628]/60">
                  <span>Base fare</span>
                  <span>KSh {baseFare}</span>
                </div>
                <div className="flex justify-between text-xs text-green-600 font-medium">
                  <span>Promo ({appliedPromo?.code_name})</span>
                  <span>- KSh {discountAmount}</span>
                </div>
                <div className="flex justify-between text-sm font-bold text-[#0a1628]">
                  <span>Total</span>
                  <span>KSh {finalFare}</span>
                </div>
              </div>
            )}
          </div>

          {/* Payment used */}
          <div className="flex items-center gap-2 mb-4 px-1">
            <span className="text-base">{selectedMethod?.icon}</span>
            <span className="text-sm text-[#0a1628]/60">Paid via <span className="font-semibold text-[#0a1628]">{selectedMethod?.label}</span></span>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button className="btn-secondary flex-1 gap-2">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.63A2 2 0 012 .18h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92z" />
              </svg>
              Call Driver
            </button>
            <button
              onClick={() => navigate('/order-tracking-screen')}
              className="btn-dark flex-1"
            >
              Track Ride
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-[#0a1628]/80 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto">
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1 sm:hidden">
          <div className="w-10 h-1 rounded-full bg-[#0a1628]/20"></div>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#0a1628]/8">
          <h3 className="text-lg font-bold text-[#0a1628]">Confirm Booking</h3>
          <button
            onClick={onClose}
            className="p-2 rounded-xl bg-[#0a1628]/5 hover:bg-[#0a1628]/10 transition-colors"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 text-[#0a1628]">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Trip Summary */}
          <div className="bg-[#0a1628]/4 rounded-2xl p-4 space-y-3">
            <div className="flex items-start gap-3">
              <div className="flex flex-col items-center gap-1 mt-1">
                <div className="w-2.5 h-2.5 rounded-full bg-[#ffd700] border-2 border-[#0a1628]"></div>
                <div className="w-0.5 h-6 bg-[#0a1628]/20"></div>
                <div className="w-2.5 h-2.5 rounded-sm bg-[#0a1628] rotate-45"></div>
              </div>
              <div className="flex-1 space-y-3">
                <div>
                  <p className="text-xs text-[#0a1628]/50 font-medium">PICKUP</p>
                  <p className="text-sm font-semibold text-[#0a1628]">{bookingData?.pickup || 'Current Location'}</p>
                </div>
                <div>
                  <p className="text-xs text-[#0a1628]/50 font-medium">DROP-OFF</p>
                  <p className="text-sm font-semibold text-[#0a1628]">{bookingData?.dropoff || 'Destination'}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-[#0a1628]/50">Vehicle</p>
                <p className="text-sm font-bold text-[#0a1628]">{vehicleLabels?.[bookingData?.vehicle] || 'Boda Boda'}</p>
                {appliedPromo ? (
                  <div className="mt-1">
                    <p className="text-sm line-through text-[#0a1628]/40">KSh {baseFare}</p>
                    <p className="text-lg font-bold text-green-600">KSh {finalFare}</p>
                  </div>
                ) : (
                  <p className="text-lg font-bold text-[#0a1628] mt-1">KSh {baseFare}</p>
                )}
              </div>
            </div>

            {/* Fare breakdown when promo applied */}
            {appliedPromo && (
              <div className="pt-3 border-t border-[#0a1628]/10 space-y-1.5">
                <div className="flex justify-between text-xs text-[#0a1628]/55">
                  <span>Base fare</span>
                  <span>KSh {baseFare}</span>
                </div>
                <div className="flex justify-between text-xs font-semibold text-green-600">
                  <span className="flex items-center gap-1">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3 h-3">
                      <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
                    </svg>
                    Promo ({appliedPromo?.code_name})
                    {appliedPromo?.discount_type === 'percentage' && ` · ${appliedPromo?.discount_value}% off`}
                  </span>
                  <span>- KSh {discountAmount}</span>
                </div>
                <div className="flex justify-between text-sm font-bold text-[#0a1628] pt-1 border-t border-[#0a1628]/8">
                  <span>You pay</span>
                  <span>KSh {finalFare}</span>
                </div>
              </div>
            )}
          </div>

          {/* Promo Code Input */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-[#0a1628]/60 uppercase tracking-wider">Promo Code</h4>

            {promoState !== 'valid' ? (
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(e) => {
                      setPromoCode(e?.target?.value?.toUpperCase());
                      if (promoState === 'invalid') { setPromoState('idle'); setPromoError(''); }
                    }}
                    onKeyDown={(e) => e?.key === 'Enter' && handleApplyPromo()}
                    placeholder="Enter promo code"
                    maxLength={20}
                    className={`w-full text-sm px-4 py-3 bg-white border rounded-xl focus:outline-none transition-colors font-mono tracking-wider uppercase ${
                      promoState === 'invalid' ? 'border-red-400 focus:border-red-400 focus:ring-1 focus:ring-red-200' : 'border-[#0a1628]/15 focus:border-[#ffd700] focus:ring-1 focus:ring-[#ffd700]/30'
                    }`}
                  />
                  {promoState === 'validating' && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <div className="w-4 h-4 border-2 border-[#0a1628]/20 border-t-[#0a1628] rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>
                <button
                  onClick={handleApplyPromo}
                  disabled={!promoCode?.trim() || promoState === 'validating'}
                  className="btn-dark px-4 flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none"
                >
                  Apply
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3 px-4 py-3 bg-green-50 border border-green-200 rounded-xl">
                <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4 text-green-600">
                    <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-green-700 font-mono">{appliedPromo?.code_name}</p>
                  <p className="text-xs text-green-600">
                    {appliedPromo?.discount_type === 'percentage'
                      ? `${appliedPromo?.discount_value}% off — saving KSh ${discountAmount}`
                      : `KSh ${discountAmount} off applied`}
                  </p>
                </div>
                <button
                  onClick={handleRemovePromo}
                  className="p-1.5 rounded-lg hover:bg-green-100 transition-colors flex-shrink-0"
                  title="Remove promo"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5 text-green-600">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}

            {/* Error message */}
            {promoState === 'invalid' && promoError && (
              <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-100 rounded-lg">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5 text-red-500 flex-shrink-0">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 8v4M12 16h.01" strokeLinecap="round" />
                </svg>
                <p className="text-xs text-red-600">{promoError}</p>
              </div>
            )}
          </div>

          {/* Payment Method — Dropdown with one-tap selection */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-[#0a1628]/60 uppercase tracking-wider">Payment Method</h4>
              {initialPaymentMethod && initialPaymentMethod === paymentMethod && (
                <span className="text-[10px] font-semibold text-[#ffd700] bg-[#ffd700]/10 px-2 py-0.5 rounded-full">Last used</span>
              )}
            </div>

            {/* Selected method display / trigger */}
            <button
              onClick={() => setDropdownOpen(prev => !prev)}
              className={`w-full flex items-center gap-3 p-3.5 rounded-xl border-2 transition-all ${
                dropdownOpen ? 'border-[#ffd700] bg-[#ffd700]/5' : 'border-[#0a1628]/10 bg-white hover:border-[#ffd700]/40'
              }`}
            >
              <span className="text-xl">{selectedMethod?.icon}</span>
              <div className="flex-1 text-left">
                <p className={`text-sm font-semibold ${selectedMethod?.color}`}>{selectedMethod?.label}</p>
                <p className="text-xs text-[#0a1628]/50">{selectedMethod?.description}</p>
              </div>
              <svg
                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                className={`w-4 h-4 text-[#0a1628]/40 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}
              >
                <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {/* Dropdown list */}
            {dropdownOpen && (
              <div className="rounded-xl border border-[#0a1628]/10 bg-white shadow-lg overflow-hidden">
                {allPaymentMethods?.map((method) => (
                  <button
                    key={method?.id}
                    onClick={() => handleSelectMethod(method?.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 transition-all hover:bg-[#ffd700]/5 ${
                      paymentMethod === method?.id ? 'bg-[#ffd700]/8' : ''
                    } ${method?.id !== allPaymentMethods?.[allPaymentMethods?.length - 1]?.id ? 'border-b border-[#0a1628]/6' : ''}`}
                  >
                    <span className="text-lg">{method?.icon}</span>
                    <div className="flex-1 text-left">
                      <p className={`text-sm font-semibold ${method?.color}`}>{method?.label}</p>
                      <p className="text-xs text-[#0a1628]/45">{method?.description}</p>
                    </div>
                    {paymentMethod === method?.id && (
                      <div className="w-5 h-5 rounded-full bg-[#ffd700] flex items-center justify-center flex-shrink-0">
                        <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-3 h-3 text-[#0a1628]">
                          <path d="M2 6l3 3 5-5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                    )}
                    {initialPaymentMethod === method?.id && paymentMethod !== method?.id && (
                      <span className="text-[9px] font-bold text-[#0a1628]/35 bg-[#0a1628]/6 px-1.5 py-0.5 rounded-full flex-shrink-0">Last used</span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {paymentMethod === 'mpesa' && (
              <div className="mt-2">
                <input
                  type="tel"
                  value={mpesaNumber}
                  onChange={(e) => setMpesaNumber(e?.target?.value)}
                  placeholder="M-Pesa number (e.g. 0712345678)"
                  className="w-full text-sm px-4 py-3 bg-white border border-[#0a1628]/15 rounded-xl focus:outline-none focus:border-[#ffd700] focus:ring-1 focus:ring-[#ffd700]/30 transition-colors"
                />
              </div>
            )}
          </div>

          {/* Confirm Button */}
          <button
            onClick={handleConfirmBooking}
            className="btn-primary-full text-base"
          >
            {appliedPromo
              ? `Confirm & Book · KSh ${finalFare}`
              : `Confirm & Book · KSh ${baseFare}`}
          </button>

          <p className="text-center text-xs text-[#0a1628]/40">
            By confirming, you agree to our Terms of Service
          </p>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmation;
